import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useLocation, useNavigate } from 'react-router-dom';
import { PinInput } from 'react-input-pin-code';
import { once, debounce } from 'lodash';

import { LoaderArrow } from 'components/Loaders/LoaderArrow';
import authService from 'store/auth/service';
import { CompanyContext } from 'index';
import ButtonWithArrowIcon from 'components/Buttons/ButtonWithArrowIcon';
import { CompanyLogo } from 'components/CompanyLogo';
import { ContentBox } from 'components/ContentBox';
import { Snackbar } from 'components/Snackbars';
import { DriverDocumentsPage } from 'routes/paths';
import { localStorageWrapper } from 'functions/localStorage';

import styles from './styles.module.scss';
import { IUser } from 'store/user/interfaces';

export const DriverLoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [values, setValues] = useState(['', '', '', '']);
  const company = useContext(CompanyContext);
  const { driverId } = useParams<{ driverId: string }>();

  const [loginCandidate, { isLoading }] =
    authService.endpoints.loginCandidate.useMutation();
  const [checkUserCandidate] = authService.useLazyCheckUserCandidateQuery();

  useEffect(() => {
    if (driverId && driverId !== 'unauthorized') {
      localStorageWrapper.clear();
    }
  }, [driverId]);

  const login = async () => {
    if (values.every((v) => v)) {
      const res = await loginCandidate({
        candidateCode: driverId || '',
        lastPhoneDigits: values.join(''),
      });
      if (!(res as any).error) {
        const checkUserCandidateResponse = await checkUserCandidate();
        if ((checkUserCandidateResponse as any).error)
          return <Snackbar message={'Invalid Credentials'} />;
        const user = checkUserCandidateResponse.data as IUser;
        window.analytics.identify(user._id, {
          created: new Date(),
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
          roles: user.roles,
          hostname: window.location.hostname,
        });

        return navigate(state?.path || DriverDocumentsPage);
      }
    }
  };

  const handleComplete = useCallback(debounce(once(login), 300), [
    JSON.stringify(values),
  ]);

  const handleLogin = () => {
    login();
  };

  if (isLoading) {
    return <LoaderArrow />;
  }

  return (
    <div className={styles.root}>
      <ContentBox className={styles.content}>
        <CompanyLogo src={company.logo} responsive={true} />
        {driverId === 'unauthorized' ? (
          <div className={styles.description}>
            {t('DriverLoginPage.descriptionForUnauthorized')}
          </div>
        ) : (
          <>
            <div className={styles.description}>
              {t('DriverLoginPage.description')}
            </div>
            <div className={styles.formWrapper}>
              <PinInput
                values={values}
                onChange={(value, index, values) => setValues(values)}
                inputStyle={{
                  fontFamily: "'SF Pro Text', sans-serif",
                  fontStyle: 'normal',
                  fontWeight: 700,
                  fontSize: '25px',
                  lineHeight: '30px',
                  border: 'none',
                  borderBottom: '1px solid #d0d3d6',
                  color: 'var(--main-color)',
                  width: '36px',
                  height: '36px',
                  outline: 'none',
                  borderRadius: 0,
                  boxShadow: 'none',
                  backgroundColor: 'transparent',
                }}
                placeholder=""
                onComplete={handleComplete}
              />
            </div>
            <ButtonWithArrowIcon
              onClick={handleLogin}
              disabled={!values.every((v) => v)}
            >
              {t('DriverLoginPage.btn.submit')}
            </ButtonWithArrowIcon>
          </>
        )}
      </ContentBox>
      <div className={styles.note}>
        {t('DriverLoginPage.note.text')}{' '}
        <a href="Public/DriverLoginPage#">{t('DriverLoginPage.note.link')}</a>
      </div>
    </div>
  );
};
